import { useCallback, useEffect, useRef, useState } from 'react';
// @ts-ignore - Cesium module resolution issue
import * as Cesium from 'cesium';
// Live ground resolution (metres per screen pixel) sampled at the bottom-centre
// of the viewport. Extracted from MapControlPanel so it can drive both the
// in-panel readout and the always-on corner scale bar. Updates on camera motion
// only, throttled, and skips React re-renders unless the value meaningfully
// changes.
export const useScaleBar = (viewer) => {
    const [metersPerPixel, setMetersPerPixel] = useState(null);
    const lastUpdateRef = useRef(0);
    const lastValRef = useRef(0);
    const computeMetersPerPixel = useCallback((v, samplePx) => {
        try {
            const scene = v.scene;
            const camera = v.camera;
            const canvas = scene.canvas;
            const ellipsoid = Cesium.Ellipsoid.WGS84;
            const px = Math.max(10, Math.min(samplePx || 100, canvas.clientWidth - 20));
            const centerX = canvas.clientWidth / 2;
            const yPos = Math.max(10, canvas.clientHeight - 10);
            const left = new Cesium.Cartesian2(centerX - px / 2, yPos);
            const right = new Cesium.Cartesian2(centerX + px / 2, yPos);
            const p1 = camera.pickEllipsoid(left, ellipsoid);
            const p2 = camera.pickEllipsoid(right, ellipsoid);
            if (p1 && p2) {
                const c1 = Cesium.Cartographic.fromCartesian(p1, ellipsoid);
                const c2 = Cesium.Cartographic.fromCartesian(p2, ellipsoid);
                const geodesic = new Cesium.EllipsoidGeodesic(c1, c2, ellipsoid);
                return geodesic.surfaceDistance / px;
            }
            // Fallback approximation using height and FOV
            const height = Math.max(1, camera.positionCartographic.height);
            const frustum = camera.frustum;
            const fov = frustum && typeof frustum.fov === 'number' ? frustum.fov : Cesium.Math.toRadians(60);
            return (2 * height * Math.tan(fov / 2)) / Math.max(1, canvas.clientHeight);
        }
        catch (_e) {
            return NaN;
        }
    }, []);
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const update = () => {
            const now = performance.now();
            if (now - lastUpdateRef.current < 100)
                return;
            lastUpdateRef.current = now;
            const mpp = computeMetersPerPixel(viewer, 100);
            if (!isFinite(mpp) || mpp <= 0)
                return;
            const last = lastValRef.current;
            if (last === 0 || Math.abs(mpp - last) / last > 0.01) {
                lastValRef.current = mpp;
                setMetersPerPixel(mpp);
            }
        };
        const camera = viewer.camera;
        const scene = viewer.scene;
        camera.changed.addEventListener(update);
        scene.preRender.addEventListener(update);
        update();
        return () => {
            try {
                camera.changed.removeEventListener(update);
            }
            catch (_e) { /* viewer torn down */ }
            try {
                scene.preRender.removeEventListener(update);
            }
            catch (_e) { /* viewer torn down */ }
        };
    }, [viewer, computeMetersPerPixel]);
    return metersPerPixel;
};
